import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import * as Styled from '../style';
import DatePicker, { registerLocale } from 'react-datepicker';
import { MdOutlineCalendarToday } from 'react-icons/md';
import { ThemeContext } from '../../../context/ThemeContext';
import { enGB } from 'date-fns/locale';
import {
  startOfWeek,
  endOfWeek,
  isAfter,
  isBefore,
  isSameWeek,
} from 'date-fns';
import { getWeekRange } from '../helper';
import store from '../../../store/store';

// Helper: Define 'today' once outside the component
const today = new Date();

/**
 * A highly customized week selector component.
 *
 * @param {string} portalId - The ID for the react-datepicker portal.
 * @param {Date} selectedWeek - The currently selected week (any date within that week).
 * @param {function} setSelectedWeek - State setter to update the selected week.
 * @param {Date | string} goLiveDate - The earliest selectable date.
 * @param {number} [weekStartsOn=2] - The day the week starts on (1=Mon, 2=Tue, etc.).
 * @param {boolean} [disabled=false] - Disables the picker.
 * @param {number | null} [restrictToLastNWeeks=null] - Restricts selection to the last N weeks.
 */
export function WeekPickerFilter({
  portalId,
  selectedWeek,
  setSelectedWeek,
  goLiveDate,
  weekStartsOn = 2,
  disabled = false,
  restrictToLastNWeeks = null,
}) {
  const { mode } = useContext(ThemeContext);
  const [formattedRange, setFormattedRange] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef(null);

  const isMFE = store.getState().app.isMFE;

  // --- Core Effect ---
  // When the selected week changes, update the parent and the display text.
  useEffect(() => {
    const { formattedRange } = getWeekRange(selectedWeek, weekStartsOn);
    setFormattedRange(formattedRange);
  }, [selectedWeek, weekStartsOn]);

  // --- Locale Registration ---
  // Memoized to prevent re-registering on every render.
  useMemo(() => {
    const customLocale = {
      ...enGB,
      options: { ...enGB.options, weekStartsOn: weekStartsOn },
      localize: {
        ...enGB.localize,
        day: (n) => ['S', 'M', 'T', 'W', 'T', 'F', 'S'][n],
      },
    };
    registerLocale('custom-enGB', customLocale);
  }, [weekStartsOn]);

  // --- Date Constraints (Memoized) ---
  const goLiveDateObj = useMemo(() => {
    // Ensure goLiveDate is a valid Date object
    return goLiveDate instanceof Date ? goLiveDate : new Date(goLiveDate);
  }, [goLiveDate]);

  const minDate = useMemo(() => {
    let effectiveMinDate = goLiveDateObj;

    if (restrictToLastNWeeks) {
      // Calculate N-1 weeks ago (because current week counts as 1)
      // Example: restrictToLastNWeeks=5 means 4 weeks ago + current week = 5 weeks total
      const nWeeksAgo = new Date(today);
      nWeeksAgo.setDate(today.getDate() - (restrictToLastNWeeks - 1) * 7);
      const minDateForLastNWeeks = startOfWeek(nWeeksAgo, {
        weekStartsOn: weekStartsOn,
      });

      // Use the more restrictive of: go-live date or N weeks ago
      // If nWeeksAgo is BEFORE goLiveDate, use goLiveDate instead
      effectiveMinDate = minDateForLastNWeeks;
      if (isBefore(minDateForLastNWeeks, goLiveDateObj)) {
        effectiveMinDate = startOfWeek(goLiveDateObj, {
          weekStartsOn: weekStartsOn,
        });
      }
    }

    // Return the start of the week for that effective date.
    return startOfWeek(effectiveMinDate, { weekStartsOn: weekStartsOn });
  }, [goLiveDateObj, restrictToLastNWeeks, weekStartsOn]);

  const maxDate = today; // Max date is always today

  // --- Handlers ---
  const handleDateChange = (date) => {
    // When a new date is picked, just update the state.
    // The `useEffect` hook above will handle the rest.
    setSelectedWeek(date);
    setIsOpen(false); // Manually close on selection
  };

  const openCalendar = () => {
    if (!disabled) {
      datePickerRef.current?.setOpen(true);
      setIsOpen(true);
    }
  };

  // --- UX: Close calendar on container scroll ---
  useEffect(() => {
    const container = document.querySelector('.tabPanel');
    if (!container || !isOpen) {
      return;
    }

    const handleScroll = () => {
      if (datePickerRef.current) {
        datePickerRef.current.setOpen(false);
        setIsOpen(false);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]); // Only run when 'isOpen' state changes

  // --- Render ---
  return (
    <Styled.DatePickerContainer mode={mode} style={{ position: 'relative' }}>
      <Styled.GlobalStyle mode={mode} />
      <Styled.DatePickerLabel
        isMFE={isMFE}
        data-testid="date-picker-label"
        onClick={openCalendar}
        mode={mode}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span>{formattedRange}</span>
        <Styled.WeeklyIconContainer mode={mode}>
          <MdOutlineCalendarToday
            data-accessible={true}
            style={{
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontSize: '16px',
            }}
            data-testid="datepicker_week_calendar"
          />
        </Styled.WeeklyIconContainer>
      </Styled.DatePickerLabel>

      <DatePicker
        ref={datePickerRef}
        selected={selectedWeek}
        portalId={portalId}
        onChange={handleDateChange}
        showWeekPicker
        locale="custom-enGB"
        minDate={minDate}
        maxDate={maxDate}
        openToDate={selectedWeek} // Open calendar to the selected week's month
        customInput={<></>} // Invisible input
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={() => setIsOpen(false)}
        disabled={disabled}
        // This filter ensures that a week is only selectable if:
        // 1. Its end date is after the go-live date (prevents partial week before go-live)
        // 2. Its start date is >= minDate (respects restrictToLastNWeeks)
        filterDate={(date) => {
          const weekStart = startOfWeek(date, { weekStartsOn: weekStartsOn });
          const weekEnd = endOfWeek(date, { weekStartsOn: weekStartsOn });
          return isAfter(weekEnd, goLiveDateObj) && weekStart >= minDate;
        }}
        popperModifiers={[
          {
            name: 'offset',
            options: { offset: [0, -15] },
          },
        ]}
        dayClassName={(date) =>
          isSameWeek(date, selectedWeek, { weekStartsOn: weekStartsOn })
            ? 'selected-week' // Class to highlight the whole week
            : undefined
        }
      />
    </Styled.DatePickerContainer>
  );
}
